// @seventide/types — health module DTOs (FR-6.x, FR-7.x, R-5.2).
// Owned by the health module (docs/ARCHITECTURE.md §6). Health data is special category data under UK GDPR:
// free text is encrypted at rest, reads by non-owners are audited and nothing here is ever sent to analytics.
import { z } from 'zod';
import { Id, IsoDate, IsoDateTime } from './common';
import { CARE_SETTINGS, CareSetting, ChcDomain } from './enums';

// ---------------------------------------------------------------- shared enums

/** Severity as the family sees it (FR-6.2) — never an assessment of the level of need. */
export const HEALTH_SEVERITIES = ['mild', 'moderate', 'severe', 'critical'] as const;
export const HealthSeverity = z.enum(HEALTH_SEVERITIES);
export type HealthSeverity = z.infer<typeof HealthSeverity>;
export const HEALTH_SEVERITY_LABELS: Record<HealthSeverity, string> = {
  mild: 'Mild',
  moderate: 'Moderate',
  severe: 'Severe',
  critical: 'Very serious',
};
export const HEALTH_SEVERITY_HINTS: Record<HealthSeverity, string> = {
  mild: 'Small problem, quickly sorted',
  moderate: 'Needed extra help or time',
  severe: 'Needed skilled help or caused harm',
  critical: 'Emergency, or risk to life',
};

/** Professional input needed (FR-6.2). */
export const HEALTH_PROFESSIONALS = ['gp', 'district_nurse', 'paramedic', 'hospital', 'other'] as const;
export const HealthProfessional = z.enum(HEALTH_PROFESSIONALS);
export type HealthProfessional = z.infer<typeof HealthProfessional>;
export const HEALTH_PROFESSIONAL_LABELS: Record<HealthProfessional, string> = {
  gp: 'GP',
  district_nurse: 'District nurse',
  paramedic: 'Paramedic or ambulance',
  hospital: 'Hospital',
  other: 'Other professional',
};

export { CARE_SETTINGS as HEALTH_SETTINGS };
export const HEALTH_SETTING_LABELS: Record<CareSetting, string> = {
  own_home: 'Their own home',
  home_with_care: 'Home with carers visiting',
  residential_home: 'Residential care home',
  nursing_home: 'Nursing home',
  hospital: 'Hospital',
  other: 'Somewhere else',
};

const text = (max: number) => z.string().trim().max(max);

// ---------------------------------------------------------------- consent gate (R-5.2)

/**
 * UK GDPR Art. 17(1)(b): once the last consent or declaration covering health information is withdrawn, the
 * health records are hidden from everyone at once and permanently deleted HEALTH_ERASURE_DAYS later, unless
 * consent covering health is recorded again before then (the records then come back).
 */
export const HealthErasure = z.object({
  /** When the records were hidden (the withdrawal). */
  hiddenAt: IsoDateTime,
  /** The day they are (or were) permanently deleted. */
  deleteOn: IsoDate,
  /** When they were deleted; null while they can still be restored. */
  deletedAt: IsoDateTime.nullable(),
});
export type HealthErasure = z.infer<typeof HealthErasure>;

export const HealthConsentStatus = z.object({
  /** A signed consent record with explicit health-data consent exists. */
  granted: z.boolean(),
  /** When it was signed (if granted). */
  signedAt: IsoDateTime.nullable(),
  /** principal_consent | attorney_declaration */
  type: z.string().nullable(),
  method: z.string().nullable(),
  lawfulBasis: z.string().nullable(),
  /** A health-data consent existed but has been withdrawn: no new health records may be added. */
  withdrawn: z.boolean(),
  /** The caller may record consent (More › Principal). */
  canRecordConsent: z.boolean(),
  /** Set when no consent covering health is in force after a withdrawal (see HealthErasure). */
  erasure: HealthErasure.nullable(),
});
export type HealthConsentStatus = z.infer<typeof HealthConsentStatus>;

/** Error code returned (403) when a write is attempted without health-data consent. */
export const HEALTH_CONSENT_REQUIRED = 'health_consent_required';
/** Error code returned (403) by health record endpoints while the records are hidden after a withdrawal. */
export const HEALTH_RECORDS_WITHDRAWN = 'health_records_withdrawn';

// ---------------------------------------------------------------- diary entries (FR-6.1–6.3)

export const HealthEntryAuthor = z.object({ id: Id, name: z.string() }).nullable();

export const HealthEntryDto = z.object({
  id: Id,
  occurredAt: IsoDateTime,
  domains: z.array(ChcDomain),
  text: z.string().nullable(),
  phrases: z.array(z.string()),
  severity: HealthSeverity.nullable(),
  observedBy: z.string().nullable(),
  professionalInvolved: z.array(HealthProfessional),
  setting: CareSetting.nullable(),
  documentCount: z.number().int(),
  createdBy: HealthEntryAuthor,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  /** Caller may edit/delete this entry. */
  canEdit: z.boolean(),
});
export type HealthEntryDto = z.infer<typeof HealthEntryDto>;

export const HealthEntryFields = z.object({
  occurredAt: IsoDateTime,
  domains: z.array(ChcDomain).min(1, 'Choose at least one care area').max(12),
  text: text(5000).optional().nullable(),
  phrases: z.array(text(200).min(1)).max(30),
  severity: HealthSeverity.optional().nullable(),
  observedBy: text(200).optional().nullable(),
  professionalInvolved: z.array(HealthProfessional).max(5),
  setting: CareSetting.optional().nullable(),
});

/** POST /health/entries — idempotent on `id`/`clientId` (ADR 0004 offline capture). */
export const CreateHealthEntryRequest = HealthEntryFields.extend({
  phrases: HealthEntryFields.shape.phrases.default([]),
  professionalInvolved: HealthEntryFields.shape.professionalInvolved.default([]),
  /** Client-generated UUID; the server uses it as the record id and returns the existing record on retry. */
  id: Id.optional(),
  clientId: Id.optional(),
  /** Linked vault documents (photos etc.). */
  documentIds: z.array(Id).max(50).optional(),
  /** How the entry was captured (analytics only, never content). */
  source: z.enum(['form', 'quick', 'voice']).optional(),
}).refine((v) => Boolean((v.text && v.text.trim()) || v.phrases.length), {
  message: 'Choose a phrase or write what happened',
  path: ['text'],
});
export type CreateHealthEntryRequest = z.input<typeof CreateHealthEntryRequest>;

export const UpdateHealthEntryRequest = HealthEntryFields.partial().extend({
  /** When the edit was made on the device (offline LWW, ADR 0004). */
  clientUpdatedAt: IsoDateTime.optional(),
});
export type UpdateHealthEntryRequest = z.input<typeof UpdateHealthEntryRequest>;

const csvList = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess(
    (v) => (typeof v === 'string' ? v.split(',').filter(Boolean) : v === undefined ? undefined : v),
    z.array(item).optional(),
  );

export const HealthEntryListQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  /** One or more domains (comma separated). */
  domain: csvList(ChcDomain),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  severity: csvList(HealthSeverity),
  /** 'any' = only entries with professional input; or a specific professional. */
  professional: z.union([z.literal('any'), z.literal('none'), HealthProfessional]).optional(),
  /** Free-text search over what happened, phrases and observer (decrypted in memory). */
  q: z.string().trim().max(200).optional(),
  sort: z.enum(['newest', 'oldest']).default('newest'),
});
export type HealthEntryListQuery = z.input<typeof HealthEntryListQuery>;

export const HealthEntryList = z.object({
  items: z.array(HealthEntryDto),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type HealthEntryList = z.infer<typeof HealthEntryList>;

// ---------------------------------------------------------------- domains (FR-6.4)

export const HealthEntryBrief = z.object({
  id: Id,
  occurredAt: IsoDateTime,
  phrases: z.array(z.string()),
  text: z.string().nullable(),
  severity: HealthSeverity.nullable(),
});
export type HealthEntryBrief = z.infer<typeof HealthEntryBrief>;

export const HealthDomainStats = z.object({
  domain: ChcDomain,
  last30Days: z.number().int(),
  last90Days: z.number().int(),
  total: z.number().int(),
  lastEntryAt: IsoDateTime.nullable(),
  /** Entries with professional input in the last 90 days. */
  professionalLast90Days: z.number().int(),
  /** Severe or very serious entries in the last 90 days. */
  severeLast90Days: z.number().int(),
  recent: z.array(HealthEntryBrief),
});
export type HealthDomainStats = z.infer<typeof HealthDomainStats>;

export const HealthDomainsResponse = z.object({
  asOf: IsoDate,
  domains: z.array(HealthDomainStats),
});
export type HealthDomainsResponse = z.infer<typeof HealthDomainsResponse>;

// ---------------------------------------------------------------- CHC process (FR-6.5, FR-6.7)

export const CHC_OUTCOMES = ['positive', 'negative'] as const;
export const ChcChecklistOutcome = z.enum(CHC_OUTCOMES);
export type ChcChecklistOutcome = z.infer<typeof ChcChecklistOutcome>;

export const CHC_DECISIONS = ['eligible', 'not_eligible', 'fnc_only'] as const;
export const ChcDecision = z.enum(CHC_DECISIONS);
export type ChcDecision = z.infer<typeof ChcDecision>;
export const CHC_DECISION_LABELS: Record<ChcDecision, string> = {
  eligible: 'Eligible',
  not_eligible: 'Not eligible',
  fnc_only: 'Not eligible, nursing care contribution instead',
};

export const CHC_REVIEW_OUTCOMES = ['upheld', 'overturned', 'pending', 'withdrawn'] as const;
export const ChcReviewOutcome = z.enum(CHC_REVIEW_OUTCOMES);
export type ChcReviewOutcome = z.infer<typeof ChcReviewOutcome>;
export const CHC_REVIEW_OUTCOME_LABELS: Record<ChcReviewOutcome, string> = {
  pending: 'Waiting for the outcome',
  upheld: 'Original decision kept',
  overturned: 'Decision changed',
  withdrawn: 'Withdrawn',
};

export const CHC_STAGES = [
  'not_started',
  'checklist_requested',
  'checklist_done',
  'assessment',
  'decision',
  'review_requested',
  'local_resolution',
  'independent_review',
  'eligible',
  'closed',
] as const;
export const ChcStage = z.enum(CHC_STAGES);
export type ChcStage = z.infer<typeof ChcStage>;

/** Editable CHC process fields (all optional — families fill them in as the process goes on). */
export const ChcProcessFields = z.object({
  checklistRequestedDate: IsoDate.nullable(),
  checklistDate: IsoDate.nullable(),
  checklistOutcome: ChcChecklistOutcome.nullable(),
  fastTrack: z.boolean(),
  fastTrackDate: IsoDate.nullable(),
  dstDate: IsoDate.nullable(),
  mdtDate: IsoDate.nullable(),
  mdtRecommendation: ChcDecision.nullable(),
  decisionDate: IsoDate.nullable(),
  decision: ChcDecision.nullable(),
  /** Reference on the decision letter (encrypted). */
  decisionReference: text(120).nullable(),
  /** Funding start date when eligible (reviews are scheduled from this). */
  eligibleFrom: IsoDate.nullable(),
  /** Date the family asked for the decision to be reviewed (local resolution). */
  reviewRequestedDate: IsoDate.nullable(),
  localResolutionDate: IsoDate.nullable(),
  localResolutionOutcome: ChcReviewOutcome.nullable(),
  independentReviewRequestedDate: IsoDate.nullable(),
  /** Deadline for asking for an independent review, from the local resolution letter. */
  independentReviewDeadline: IsoDate.nullable(),
  independentReviewDate: IsoDate.nullable(),
  independentReviewOutcome: ChcReviewOutcome.nullable(),
  /** Last review that took place (eligible cases). */
  lastReviewDate: IsoDate.nullable(),
  closed: z.boolean(),
  /** Free-text notes (encrypted). */
  notes: text(5000).nullable(),
});
export type ChcProcessFields = z.infer<typeof ChcProcessFields>;

export const UpdateChcProcessRequest = ChcProcessFields.partial();
export type UpdateChcProcessRequest = z.input<typeof UpdateChcProcessRequest>;

export const ChcTimelineStatus = z.enum(['done', 'current', 'upcoming', 'overdue', 'not_applicable']);
export type ChcTimelineStatus = z.infer<typeof ChcTimelineStatus>;

export const ChcTimelineItem = z.object({
  key: z.string(),
  label: z.string(),
  date: IsoDate.nullable(),
  status: ChcTimelineStatus,
  detail: z.string().nullable(),
  /** Explanation id for an InfoButton. */
  ruleId: z.string().nullable(),
  isDeadline: z.boolean(),
});
export type ChcTimelineItem = z.infer<typeof ChcTimelineItem>;

export const ChcExplanation = z.object({ ruleId: z.string(), text: z.string() });

export const ChcProcessDto = ChcProcessFields.extend({
  id: Id.nullable(),
  stage: ChcStage,
  nextReviewDate: IsoDate.nullable(),
  reviewDates: z.array(IsoDate),
  appealDeadline: IsoDate.nullable(),
  /** Review-request deadline is local policy (label it "check your ICB"). */
  appealDeadlineIsLocalPolicy: z.boolean(),
  /** Saved narrative statement for the evidence pack (encrypted). */
  narrative: z.string().nullable(),
  updatedAt: IsoDateTime.nullable(),
  timeline: z.array(ChcTimelineItem),
  explanations: z.array(ChcExplanation),
  /** An assessment or review is within the next 8 weeks: prompt for records (FR-6.7). */
  prepareForAssessment: z.object({ date: IsoDate, kind: z.enum(['assessment', 'review']) }).nullable(),
  terminology: z.object({
    programmeName: z.string(),
    programmeShortName: z.string(),
    decisionBody: z.string(),
    englishProcess: z.boolean(),
    guidanceSnippetId: z.string().nullable(),
    overviewExplanationId: z.string(),
  }),
  partialRules: z.boolean(),
  canEdit: z.boolean(),
});
export type ChcProcessDto = z.infer<typeof ChcProcessDto>;

// ---------------------------------------------------------------- template letters (FR-6.7)

export const HealthLetterContext = z.object({
  /** Values for content `renderLetter()` placeholders, pre-filled from the workspace (user can edit). */
  values: z.record(z.string(), z.unknown()),
  nation: z.string(),
});
export type HealthLetterContext = z.infer<typeof HealthLetterContext>;

export const HealthLetterPdfRequest = z.object({
  templateId: z.string().max(80),
  title: text(200).min(1),
  text: z.string().max(20_000).min(1),
});
export type HealthLetterPdfRequest = z.infer<typeof HealthLetterPdfRequest>;

// ---------------------------------------------------------------- evidence pack (FR-6.6, FR-6.8)

export const EvidencePackRequest = z
  .object({
    from: IsoDate,
    to: IsoDate,
    /** Empty or missing = all domains. */
    domains: z.array(ChcDomain).max(12).optional(),
    includePhotos: z.boolean().default(true),
    /** Family's narrative statement (encrypted when saved). */
    narrative: z.string().max(20_000).optional().nullable(),
    /** Save the narrative on the CHC process for next time. */
    saveNarrative: z.boolean().default(true),
    format: z.enum(['pdf', 'csv']).default('pdf'),
  })
  .refine((v) => v.from <= v.to, { message: 'The start date must be before the end date', path: ['to'] });
export type EvidencePackRequest = z.input<typeof EvidencePackRequest>;

export const EvidencePackPreview = z.object({
  entries: z.number().int(),
  photos: z.number().int(),
  documents: z.number().int(),
  weeks: z.number().int(),
  byDomain: z.array(z.object({ domain: ChcDomain, count: z.number().int() })),
});
export type EvidencePackPreview = z.infer<typeof EvidencePackPreview>;

// ---------------------------------------------------------------- Attendance Allowance (FR-7.1–7.4)

export const AA_NEEDS = [
  'washing',
  'dressing',
  'toileting',
  'eating',
  'medication',
  'mobility',
  'supervision',
  'night_needs',
] as const;
export const AaNeed = z.enum(AA_NEEDS);
export type AaNeed = z.infer<typeof AaNeed>;
export const AA_NEED_LABELS: Record<AaNeed, string> = {
  washing: 'Washing and bathing',
  dressing: 'Dressing',
  toileting: 'Using the toilet',
  eating: 'Eating and drinking',
  medication: 'Medication',
  mobility: 'Moving around',
  supervision: 'Supervision to stay safe',
  night_needs: 'Help at night',
};

export const TIMES_OF_DAY = ['morning', 'afternoon', 'evening', 'night'] as const;
export const TimeOfDay = z.enum(TIMES_OF_DAY);
export type TimeOfDay = z.infer<typeof TimeOfDay>;
export const TIME_OF_DAY_LABELS: Record<TimeOfDay, string> = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
  night: 'Night',
};

export const AaScreenerRequest = z.object({
  needsHelpPersonalCareOrSupervision: z.boolean(),
  needsAtNight: z.boolean(),
  monthsNeeded: z.number().int().min(0).max(600),
  onPipOrDla: z.boolean(),
  terminallyIll: z.boolean(),
  /** Used only when no date of birth is on the Principal profile. */
  ageYears: z.number().int().min(0).max(130).optional().nullable(),
});
export type AaScreenerRequest = z.infer<typeof AaScreenerRequest>;

export const AaScreenerResponse = z.object({
  outcome: z.enum(['likely_eligible', 'not_eligible', 'check']),
  benefitName: z.string(),
  ageStatus: z.enum(['reached', 'not_reached', 'unknown']),
  statePensionAgeDate: IsoDate.nullable(),
  likelyRate: z.enum(['lower', 'higher']).nullable(),
  likelyWeeklyPence: z.number().int().nullable(),
  rates: z.object({ lowerWeeklyPence: z.number().int(), higherWeeklyPence: z.number().int() }),
  explanations: z.array(ChcExplanation),
  dateOfBirthKnown: z.boolean(),
});
export type AaScreenerResponse = z.infer<typeof AaScreenerResponse>;

export const CareNeedsEntryDto = z.object({
  id: Id,
  occurredOn: IsoDate,
  timeOfDay: TimeOfDay.nullable(),
  need: AaNeed,
  description: z.string().nullable(),
  minutes: z.number().int().nullable(),
  linkedHealthEntryId: Id.nullable(),
  createdBy: HealthEntryAuthor,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  canEdit: z.boolean(),
});
export type CareNeedsEntryDto = z.infer<typeof CareNeedsEntryDto>;

export const CareNeedsFields = z.object({
  occurredOn: IsoDate,
  timeOfDay: TimeOfDay.nullable().optional(),
  need: AaNeed,
  description: text(3000).nullable().optional(),
  minutes: z.number().int().min(0).max(24 * 60).nullable().optional(),
  linkedHealthEntryId: Id.nullable().optional(),
});
export const CreateCareNeedsEntryRequest = CareNeedsFields.extend({
  id: Id.optional(),
  clientId: Id.optional(),
});
export type CreateCareNeedsEntryRequest = z.input<typeof CreateCareNeedsEntryRequest>;
export const UpdateCareNeedsEntryRequest = CareNeedsFields.partial().extend({
  clientUpdatedAt: IsoDateTime.optional(),
});
export type UpdateCareNeedsEntryRequest = z.input<typeof UpdateCareNeedsEntryRequest>;

/** Create care-needs entries from CHC diary entries (FR-7.2 "reusing CHC diary entries"). */
export const ImportCareNeedsRequest = z.object({
  items: z
    .array(z.object({ healthEntryId: Id, need: AaNeed, timeOfDay: TimeOfDay.nullable().optional() }))
    .min(1)
    .max(100),
});
export type ImportCareNeedsRequest = z.infer<typeof ImportCareNeedsRequest>;

export const CareNeedsListQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(100),
  offset: z.coerce.number().int().min(0).default(0),
  need: AaNeed.optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
});
export type CareNeedsListQuery = z.input<typeof CareNeedsListQuery>;

export const CareNeedsList = z.object({
  items: z.array(CareNeedsEntryDto),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type CareNeedsList = z.infer<typeof CareNeedsList>;

export const AaSummarySection = z.object({
  need: AaNeed,
  title: z.string(),
  claimFormSection: z.string(),
  period: z.enum(['day', 'night']),
  entries: z.number().int(),
  daysWithHelp: z.number().int(),
  byTimeOfDay: z.record(z.string(), z.number().int()),
  totalMinutes: z.number().int(),
  averageMinutesPerDay: z.number().int().nullable(),
  examples: z.array(z.object({ date: IsoDate, timeOfDay: TimeOfDay.nullable(), text: z.string() })),
  relatedChcEntries: z.number().int(),
  relatedChcExamples: z.array(z.object({ date: IsoDate, text: z.string() })),
});
export type AaSummarySection = z.infer<typeof AaSummarySection>;

export const AaSummaryResponse = z.object({
  from: IsoDate,
  to: IsoDate,
  days: z.number().int(),
  daysRecorded: z.number().int(),
  sections: z.array(AaSummarySection),
  dayNeeds: z.boolean(),
  nightNeeds: z.boolean(),
});
export type AaSummaryResponse = z.infer<typeof AaSummaryResponse>;

export const BENEFITS = [
  'attendance_allowance',
  'pension_credit',
  'council_tax_reduction',
  'carers_allowance',
  'other',
] as const;
export const BenefitKind = z.enum(BENEFITS);
export type BenefitKind = z.infer<typeof BenefitKind>;
export const BENEFIT_LABELS: Record<BenefitKind, string> = {
  attendance_allowance: 'Attendance Allowance',
  pension_credit: 'Pension Credit',
  council_tax_reduction: 'Council Tax Reduction',
  carers_allowance: "Carer's Allowance",
  other: 'Other benefit',
};

export const CLAIM_STATUSES = [
  'not_started',
  'form_requested',
  'form_sent',
  'awarded',
  'refused',
  'reconsideration',
  'appeal',
  'withdrawn',
] as const;
export const ClaimStatus = z.enum(CLAIM_STATUSES);
export type ClaimStatus = z.infer<typeof ClaimStatus>;
export const CLAIM_STATUS_LABELS: Record<ClaimStatus, string> = {
  not_started: 'Not started',
  form_requested: 'Form requested',
  form_sent: 'Form sent, waiting for a decision',
  awarded: 'Awarded',
  refused: 'Refused',
  reconsideration: 'Asked them to look again',
  appeal: 'Appeal',
  withdrawn: 'Withdrawn',
};

export const AWARD_RATES = ['lower', 'higher'] as const;
export const AwardRate = z.enum(AWARD_RATES);
export type AwardRate = z.infer<typeof AwardRate>;

export const BenefitClaimDto = z.object({
  id: Id,
  benefit: BenefitKind,
  benefitName: z.string(),
  callDate: IsoDate.nullable(),
  formSentDate: IsoDate.nullable(),
  formDueDate: IsoDate.nullable(),
  decisionDate: IsoDate.nullable(),
  rate: AwardRate.nullable(),
  awardStartDate: IsoDate.nullable(),
  status: ClaimStatus,
  notes: z.string().nullable(),
  /** Weekly amount for the awarded rate from the current rule set (display only). */
  weeklyPence: z.number().int().nullable(),
  explanations: z.array(ChcExplanation),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type BenefitClaimDto = z.infer<typeof BenefitClaimDto>;

export const BenefitClaimFields = z.object({
  benefit: BenefitKind,
  callDate: IsoDate.nullable().optional(),
  formSentDate: IsoDate.nullable().optional(),
  /** Override the computed return-by date (e.g. the date printed on the form). */
  formDueDate: IsoDate.nullable().optional(),
  decisionDate: IsoDate.nullable().optional(),
  rate: AwardRate.nullable().optional(),
  awardStartDate: IsoDate.nullable().optional(),
  status: ClaimStatus.optional(),
  notes: text(3000).nullable().optional(),
});
export const CreateBenefitClaimRequest = BenefitClaimFields;
export type CreateBenefitClaimRequest = z.input<typeof CreateBenefitClaimRequest>;
export const UpdateBenefitClaimRequest = BenefitClaimFields.partial();
export type UpdateBenefitClaimRequest = z.input<typeof UpdateBenefitClaimRequest>;

export const AaOverview = z.object({
  benefitName: z.string(),
  dateOfBirthKnown: z.boolean(),
  statePensionAgeDate: IsoDate.nullable(),
  rates: z.object({ lowerWeeklyPence: z.number().int(), higherWeeklyPence: z.number().int() }),
  claimFormReturnWeeks: z.number().int(),
  careHomeStopDays: z.number().int(),
  claims: z.array(BenefitClaimDto),
  /** An AA claim has been awarded: prompt for knock-on entitlements (FR-7.4). */
  awarded: z.boolean(),
  canEdit: z.boolean(),
});
export type AaOverview = z.infer<typeof AaOverview>;

// ---------------------------------------------------------------- Home summary (ARCHITECTURE §7)

export const HealthSummary = z.union([
  z.object({ available: z.literal(false) }),
  z.object({
    available: z.literal(true),
    entriesLast30Days: z.number().int(),
    nextReviewDate: IsoDate.nullable(),
    appealDeadline: IsoDate.nullable(),
    consentGranted: z.boolean(),
    lastEntryAt: IsoDateTime.nullable(),
    aaFormDueDate: IsoDate.nullable(),
  }),
]);
export type HealthSummary = z.infer<typeof HealthSummary>;
